import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FaCreditCard, FaSync, FaUpload, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ErpTemplate from '../../../../components/ErpTemplate';
import api from '../../../../services/api';
import { moeda } from '../../../../util/util';
import { FinField, FinInput } from '../../../../components/FinField';
import moment from 'moment';
import '../../../../css/financeiro-parcela-pagar.css';

// Página do OPERADOR (Eventos) — pagamento por cartão de crédito.
// Atende DOIS fluxos com a mesma tela (mesmo shape de contexto no backend):
//   /pagamentoCartao/:parcelaId          → Contas Pendentes (financeiroparcela);
//                                          comprovante NÃO dá baixa (Financeiro confirma).
//   /pagamentoCartaoOrcamento/:parcelaId → Efetivo (orcamento_pagamento_parcela);
//                                          comprovante dá baixa automática (fluxo original).
// Acessada pelo link do e-mail de aviso ou pelos links em tela (Efetivo).
// Fluxo: solicitar código (OTP por e-mail) → digitar → ver dados do cartão →
// pagar o fornecedor → anexar o comprovante.
export default function PagamentoCartao() {
    const { parcelaId } = useParams();
    const token = localStorage.getItem('token');
    const ehOrcamento = window.location.pathname.indexOf('/pagamentoCartaoOrcamento/') === 0;
    const apiBase = ehOrcamento ? '/orcamentoPagamentoCartao' : '/financeiroParcelaCartao';

    const [contexto, setContexto] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);

    const [otp, setOtp] = useState('');
    const [dadosCartao, setDadosCartao] = useState(null);
    const [aviso, setAviso] = useState('');
    const [loading, setLoading] = useState(false);
    const [fileComprovante, setFileComprovante] = useState(null);
    const [enviandoComprovante, setEnviandoComprovante] = useState(false);
    const [comprovanteEnviado, setComprovanteEnviado] = useState(false);

    const carregarContexto = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: apiBase + '/' + parcelaId + '/contexto', token })
            .then(response => {
                setContexto(response.data);
                setErro(null);
            })
            .catch(error => {
                const m = error.response?.data?.mensagem ?? 'Parcela não encontrada ou sem acesso.';
                setErro(m);
            })
            .finally(() => setCarregando(false));
    }, [parcelaId, token]);

    useEffect(() => { carregarContexto(); }, [carregarContexto]);

    const solicitarCodigo = () => {
        setLoading(true);
        api({ method: 'post', action: apiBase + '/' + parcelaId + '/gerarOtp', token, data: {} })
            .then(() => toast.success('Código enviado ao seu e-mail. Validade: 10 minutos.'))
            .catch(error => {
                const m = error.response?.data?.mensagem ?? 'Erro ao enviar o código.';
                toast.error(m);
            })
            .finally(() => setLoading(false));
    };

    const verDados = () => {
        if (!otp || otp.length !== 6) {
            toast.warning('Informe o código de 6 dígitos.');
            return;
        }
        setLoading(true);
        api({ method: 'post', action: apiBase + '/' + parcelaId + '/revelar', token, data: { otp: otp } })
            .then(response => {
                setDadosCartao(response.data.dados);
                setAviso(response.data.aviso ?? '');
            })
            .catch(error => {
                const m = error.response?.data?.mensagem ?? 'Código inválido ou expirado.';
                toast.error(m);
            })
            .finally(() => setLoading(false));
    };

    const ocultarDados = () => {
        // Burn-after-read: avisa o backend para purgar os dados criptografados
        api({ method: 'post', action: apiBase + '/' + parcelaId + '/fechar', token, data: {} }).catch(() => { });
        setDadosCartao(null);
        setAviso('');
        setOtp('');
    };

    // Ao sair da página com dados revelados, garante a purga no backend
    useEffect(() => {
        return () => {
            if (dadosCartao) {
                api({ method: 'post', action: apiBase + '/' + parcelaId + '/fechar', token, data: {} }).catch(() => { });
            }
        };
    }, [dadosCartao, parcelaId, token]);

    const enviarComprovante = () => {
        if (!fileComprovante) {
            toast.warning('Selecione o arquivo do comprovante.');
            return;
        }
        // Bloqueio de tamanho no cliente (10 MB), igual ao limite do backend (max:10240 KB) —
        // dá feedback imediato em vez de subir o arquivo inteiro e falhar só no servidor.
        const MAX_BYTES = 10 * 1024 * 1024;
        if (fileComprovante.size > MAX_BYTES) {
            toast.error('Arquivo muito grande (máx. 10 MB). Reduza o tamanho e tente novamente.');
            return;
        }
        setEnviandoComprovante(true);
        const formData = new FormData();
        formData.append('comprovante', fileComprovante);
        api({ method: 'post', action: apiBase + '/' + parcelaId + '/uploadComprovante', token, data: formData })
            .then(response => {
                toast.success(response.data?.mensagem ?? 'Comprovante enviado. O financeiro foi notificado.');
                setComprovanteEnviado(true);
                setFileComprovante(null);
                if (dadosCartao) ocultarDados();
                carregarContexto();
            })
            .catch(error => {
                const m = error.response?.data?.mensagem ?? 'Erro ao enviar o comprovante.';
                toast.error(m);
            })
            .finally(() => setEnviandoComprovante(false));
    };

    const parcela = contexto?.parcela;
    const evento = contexto?.evento;
    const cartao = contexto?.cartao;
    const jaTemComprovante = comprovanteEnviado || !!cartao?.arquivo_comprovante_id;

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Pagamento por Cartão</h1>
                        <p className="fin-pp__subtitle">
                            {evento?.nome ? evento.nome + ' · ' : ''}{parcela?.descricao ?? ''}
                        </p>
                    </div>
                </div>

                {carregando && (
                    <div className="fin-pp__card" style={{ padding: 24 }}>Carregando...</div>
                )}

                {!carregando && erro && (
                    <div className="fin-pp__card" style={{ padding: 24 }}>
                        <p className="mb-0">⚠️ {erro}</p>
                    </div>
                )}

                {!carregando && !erro && contexto && (
                    <div className="row">
                        <div className="col-lg-7">
                            {/* ── Dados do pagamento ── */}
                            <div className="fin-pp__card" style={{ padding: 20, marginBottom: 16 }}>
                                <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                    <span className="fin-pp__cphead-ic"><FaCreditCard size={15} /></span>
                                    <div>
                                        <div className="fin-pp__cphead-title">Dados do pagamento</div>
                                        <div className="fin-pp__cphead-sub">Pague o fornecedor com o cartão abaixo e anexe o comprovante</div>
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-3 mb-2">
                                    {parcela?.valor && (
                                        <div><span className="text-muted small d-block">Valor</span><strong>{moeda(parcela.valor)}</strong></div>
                                    )}
                                    {parcela?.vencimento && (
                                        <div><span className="text-muted small d-block">Vencimento</span><strong>{moment(parcela.vencimento).format('DD/MM/YYYY')}</strong></div>
                                    )}
                                    {evento?.nome && (
                                        <div><span className="text-muted small d-block">Evento</span><strong>{evento.nome}</strong></div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {cartao
                                        ? <span className="fin-pp__pill is-ok">🔐 Dados do cartão disponíveis</span>
                                        : <span className="fin-pp__pill is-muted">Aguardando o Financeiro cadastrar os dados</span>}
                                    {jaTemComprovante && <span className="fin-pp__pill is-info">Comprovante enviado</span>}
                                </div>
                            </div>

                            {/* ── Ver dados do cartão (OTP) ── */}
                            {cartao && (
                                <div className="fin-pp__card" style={{ padding: 20, marginBottom: 16 }}>
                                    {!dadosCartao ? (
                                        <>
                                            <p className="small text-muted mb-2">
                                                Solicite o código de acesso — ele chega no seu e-mail, vale por <strong>10 minutos</strong> e é de <strong>uso único</strong>. Solicite apenas quando for efetuar o pagamento.
                                            </p>
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm mb-3" disabled={loading} onClick={solicitarCodigo}>
                                                <FaSync size={12} /> {loading ? 'Enviando...' : 'Receber código por e-mail'}
                                            </button>
                                            <div className="fin-pp__filters">
                                                <FinField label="Código recebido por e-mail">
                                                    <FinInput value={otp} placeholder="000000" maxLength={6}
                                                        style={{ letterSpacing: '0.4em', fontFamily: 'monospace' }}
                                                        onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} />
                                                </FinField>
                                            </div>
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary mt-2"
                                                disabled={loading || otp.length !== 6} onClick={verDados}>
                                                {loading ? 'Validando...' : 'Ver dados do cartão'}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="p-3 rounded mb-2" style={{ background: 'var(--fin-sf2, #f1f5f9)', border: '1px solid var(--fin-acb)', fontFamily: 'monospace' }}>
                                                <div><strong>Número:</strong> {dadosCartao.numero}</div>
                                                <div><strong>Titular:</strong> {dadosCartao.titular}</div>
                                                <div><strong>Validade:</strong> {dadosCartao.validade}</div>
                                                {dadosCartao.cvv && <div><strong>CVV:</strong> {dadosCartao.cvv} <span className="small">(removido após esta leitura)</span></div>}
                                            </div>
                                            {aviso && (
                                                <div className="p-2 rounded small mb-2" style={{ background: '#fff3cd', border: '1px solid #ffc107', color: '#664d03' }}>
                                                    ⚠️ {aviso}
                                                </div>
                                            )}
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={ocultarDados}>
                                                Ocultar dados
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── Comprovante ── */}
                            {cartao && (
                                <div className="fin-pp__card" style={{ padding: 20 }}>
                                    <p className="small text-muted mb-2">
                                        <strong>Após pagar o fornecedor</strong>, anexe o comprovante (PDF, JPG ou PNG). {ehOrcamento
                                            ? 'O comprovante é validado e a baixa é registrada automaticamente; o Financeiro é notificado.'
                                            : 'A parcela permanece em Contas Pendentes até o Financeiro confirmar o pagamento.'}
                                    </p>
                                    {jaTemComprovante && (
                                        <p className="small mb-2" style={{ color: 'var(--fin-ok)' }}>
                                            <FaCheckCircle size={13} /> Comprovante já enviado — anexar um novo substitui o anterior.
                                        </p>
                                    )}
                                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="form-control form-control-sm mb-2"
                                        onChange={e => setFileComprovante(e.target.files[0] ?? null)} />
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--ok"
                                        disabled={!fileComprovante || enviandoComprovante} onClick={enviarComprovante}>
                                        <FaUpload size={12} /> {enviandoComprovante ? 'Enviando...' : 'Paguei — Enviar comprovante'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
